import { useState, useEffect } from 'react';
import { useAdminData } from '../context/AdminDataContext';
import { Save, FileText, Image, Layout, Phone, Plus, X, Users } from 'lucide-react';
import { toast } from 'sonner';
import FileManagerModal from '../components/FileManagerModal';

function SectionCard({ title, icon: Icon, children, onSave, saving }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-[#47ace2]/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#47ace2]" />
          </div>
          <h2 className="text-slate-700 font-semibold font-outfit">{title}</h2>
        </div>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-xl font-manrope text-sm font-medium transition-colors disabled:opacity-60 shadow-sm"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-slate-500 text-xs font-manrope font-medium mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputClass = "w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-slate-700 text-sm font-manrope placeholder-slate-400 focus:outline-none focus:border-[#47ace2] focus:ring-2 focus:ring-[#47ace2]/10 transition-colors";
const textareaClass = inputClass + " resize-none";

export default function ContentPage() {
  const { contentSections, updateContentSection } = useAdminData();
  const [sections, setSections] = useState(contentSections);
  const [saving, setSaving] = useState({});
  const [browseTeamIndex, setBrowseTeamIndex] = useState(null);
  const [browseServiceIndex, setBrowseServiceIndex] = useState(null);

  // Sync with API data when it loads
  useEffect(() => {
    if (contentSections && Object.keys(contentSections).length > 0) {
      setSections(contentSections);
    }
  }, [contentSections]);

  const updateField = (section, key, value) => {
    setSections(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const handleSave = async (section) => {
    setSaving(prev => ({ ...prev, [section]: true }));
    try {
      await updateContentSection(section, sections[section]);
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} section saved!`);
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(prev => ({ ...prev, [section]: false }));
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-outfit">Content Manager</h1>
        <p className="text-slate-400 font-manrope text-sm mt-1">Manage dynamic homepage and website content sections</p>
      </div>

      {/* Hero Section */}
      <SectionCard title="Hero Section" icon={Image} onSave={() => handleSave('hero')} saving={saving.hero}>
        <div className="space-y-4">
          <Field label="Main Heading">
            <input type="text" value={sections.hero?.heading || ''} onChange={e => updateField('hero', 'heading', e.target.value)} placeholder="Building Tomorrow's Landmarks" className={inputClass} />
          </Field>
          <Field label="Subheading">
            <input type="text" value={sections.hero?.subheading || ''} onChange={e => updateField('hero', 'subheading', e.target.value)} placeholder="Premium Residential & Commercial Projects" className={inputClass} />
          </Field>
          <Field label="CTA Button Text">
            <input type="text" value={sections.hero?.ctaText || ''} onChange={e => updateField('hero', 'ctaText', e.target.value)} placeholder="Explore Projects" className={inputClass} />
          </Field>
        </div>
      </SectionCard>

      {/* About Section */}
      <SectionCard title="About Section" icon={Layout} onSave={() => handleSave('about')} saving={saving.about}>
        <div className="space-y-4">
          <Field label="Section Heading">
            <input type="text" value={sections.about?.heading || ''} onChange={e => updateField('about', 'heading', e.target.value)} placeholder="Building Dreams, Creating Landmarks" className={inputClass} />
          </Field>
          <Field label="About Body Text">
            <textarea rows={5} value={sections.about?.body || ''} onChange={e => updateField('about', 'body', e.target.value)} placeholder="Description of the company..." className={textareaClass} />
          </Field>
        </div>
      </SectionCard>

      {/* About Us Page Content & Team Members */}
      <SectionCard title="About Us Page" icon={Users} onSave={() => handleSave('about_page')} saving={saving.about_page}>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Main Page Heading">
              <input type="text" value={sections.about_page?.heading || ''} onChange={e => updateField('about_page', 'heading', e.target.value)} placeholder="Building Trust Since 1998" className={inputClass} />
            </Field>
            <Field label="Page Subheading">
              <input type="text" value={sections.about_page?.subheading || ''} onChange={e => updateField('about_page', 'subheading', e.target.value)} placeholder="Modern Arch has been at the forefront..." className={inputClass} />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Story Section Title">
              <input type="text" value={sections.about_page?.story_title || ''} onChange={e => updateField('about_page', 'story_title', e.target.value)} placeholder="Our Story" className={inputClass} />
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <Field label="Our Story Paragraph 1">
              <textarea rows={4} value={sections.about_page?.story_paragraph_1 || ''} onChange={e => updateField('about_page', 'story_paragraph_1', e.target.value)} placeholder="Paragraph 1 content..." className={textareaClass} />
            </Field>
            <Field label="Our Story Paragraph 2">
              <textarea rows={4} value={sections.about_page?.story_paragraph_2 || ''} onChange={e => updateField('about_page', 'story_paragraph_2', e.target.value)} placeholder="Paragraph 2 content..." className={textareaClass} />
            </Field>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-2">
              <label className="text-slate-600 font-semibold font-manrope text-sm flex items-center gap-2">
                <Users className="w-4 h-4 text-[#47ace2]" /> Leadership / Team Members
              </label>
              <button
                type="button"
                onClick={() => updateField('about_page', 'team', [...(sections.about_page?.team || []), { name: '', position: '', bio: '', image: '' }])}
                className="text-[#47ace2] text-xs font-manrope hover:underline flex items-center gap-1 font-semibold"
              >
                <Plus className="w-3 h-3" /> Add Team Member
              </button>
            </div>
            
            {(sections.about_page?.team || []).map((member, i) => (
              <div key={i} className="p-4 border border-gray-100 rounded-2xl bg-gray-50/30 space-y-3 relative group">
                <button
                  type="button"
                  onClick={() => updateField('about_page', 'team', sections.about_page.team.filter((_, idx) => idx !== i))}
                  className="absolute top-4 right-4 p-1 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Name">
                    <input
                      type="text"
                      value={member.name || ''}
                      onChange={e => {
                        const updated = [...sections.about_page.team];
                        updated[i] = { ...updated[i], name: e.target.value };
                        updateField('about_page', 'team', updated);
                      }}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Position">
                    <input
                      type="text"
                      value={member.position || ''}
                      onChange={e => {
                        const updated = [...sections.about_page.team];
                        updated[i] = { ...updated[i], position: e.target.value };
                        updateField('about_page', 'team', updated);
                      }}
                      className={inputClass}
                    />
                  </Field>
                </div>
                <Field label="Image URL">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      placeholder="Click Browse to select image"
                      value={member.image || ''}
                      onClick={() => setBrowseTeamIndex(i)}
                      className={inputClass + " cursor-pointer flex-1 pr-10"}
                    />
                    <button
                      type="button"
                      onClick={() => setBrowseTeamIndex(i)}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-sm font-medium transition-colors"
                    >
                      Browse
                    </button>
                  </div>
                </Field>
                <Field label="Bio">
                  <textarea
                    rows={2}
                    value={member.bio || ''}
                    onChange={e => {
                      const updated = [...sections.about_page.team];
                      updated[i] = { ...updated[i], bio: e.target.value };
                      updateField('about_page', 'team', updated);
                    }}
                    className={textareaClass}
                  />
                </Field>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* CTA Section */}
      <SectionCard title="CTA Section" icon={Phone} onSave={() => handleSave('cta')} saving={saving.cta}>
        <div className="space-y-4">
          <Field label="CTA Heading">
            <input type="text" value={sections.cta?.heading || ''} onChange={e => updateField('cta', 'heading', e.target.value)} placeholder="Ready to Find Your Dream Home?" className={inputClass} />
          </Field>
          <Field label="CTA Subheading">
            <input type="text" value={sections.cta?.subheading || ''} onChange={e => updateField('cta', 'subheading', e.target.value)} placeholder="Get in touch with our experts today." className={inputClass} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Primary CTA Text">
              <input type="text" value={sections.cta?.primaryCta || ''} onChange={e => updateField('cta', 'primaryCta', e.target.value)} placeholder="Call Now" className={inputClass} />
            </Field>
            <Field label="Secondary CTA Text">
              <input type="text" value={sections.cta?.secondaryCta || ''} onChange={e => updateField('cta', 'secondaryCta', e.target.value)} placeholder="Browse Projects" className={inputClass} />
            </Field>
          </div>
        </div>
      </SectionCard>

      {/* Services Page Content Manager */}
      <SectionCard title="Services Section" icon={FileText} onSave={() => handleSave('services_page')} saving={saving.services_page}>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Main Services Heading">
              <input type="text" value={sections.services_page?.heading || ''} onChange={e => updateField('services_page', 'heading', e.target.value)} placeholder="Our Services" className={inputClass} />
            </Field>
            <Field label="Services Subheading">
              <input type="text" value={sections.services_page?.subheading || ''} onChange={e => updateField('services_page', 'subheading', e.target.value)} placeholder="From concept to completion..." className={inputClass} />
            </Field>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-2">
              <label className="text-slate-600 font-semibold font-manrope text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#47ace2]" /> Services List
              </label>
              <button
                type="button"
                onClick={() => {
                  const newServiceId = 'service-' + Date.now();
                  updateField('services_page', 'services', [
                    ...(sections.services_page?.services || []),
                    { id: newServiceId, title: '', icon: 'Building2', shortDesc: '', fullDesc: '', image: '', features: [''] }
                  ]);
                }}
                className="text-[#47ace2] text-xs font-manrope hover:underline flex items-center gap-1 font-semibold"
              >
                <Plus className="w-3 h-3" /> Add Service
              </button>
            </div>
            
            {(sections.services_page?.services || []).map((service, i) => (
              <div key={i} className="p-4 border border-gray-100 rounded-2xl bg-gray-50/30 space-y-3 relative">
                <button
                  type="button"
                  onClick={() => updateField('services_page', 'services', sections.services_page.services.filter((_, idx) => idx !== i))}
                  className="absolute top-4 right-4 p-1 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Service Title">
                    <input
                      type="text"
                      value={service.title || ''}
                      onChange={e => {
                        const updated = [...sections.services_page.services];
                        updated[i] = { ...updated[i], title: e.target.value };
                        updateField('services_page', 'services', updated);
                      }}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Icon Class">
                    <select
                      value={service.icon || 'Building2'}
                      onChange={e => {
                        const updated = [...sections.services_page.services];
                        updated[i] = { ...updated[i], icon: e.target.value };
                        updateField('services_page', 'services', updated);
                      }}
                      className={inputClass}
                    >
                      <option value="Building2">Building2 (Office/Commercial)</option>
                      <option value="Landmark">Landmark (Trust/Luxury/Villa)</option>
                      <option value="Users">Users (Consulting/Support)</option>
                    </select>
                  </Field>
                </div>
                <Field label="Short Description">
                  <input
                    type="text"
                    value={service.shortDesc || ''}
                    onChange={e => {
                      const updated = [...sections.services_page.services];
                      updated[i] = { ...updated[i], shortDesc: e.target.value };
                      updateField('services_page', 'services', updated);
                    }}
                    className={inputClass}
                  />
                </Field>
                <Field label="Full Description">
                  <textarea
                    rows={3}
                    value={service.fullDesc || ''}
                    onChange={e => {
                      const updated = [...sections.services_page.services];
                      updated[i] = { ...updated[i], fullDesc: e.target.value };
                      updateField('services_page', 'services', updated);
                    }}
                    className={textareaClass}
                  />
                </Field>
                <Field label="Image URL">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      placeholder="Click Browse to select image"
                      value={service.image || ''}
                      onClick={() => setBrowseServiceIndex(i)}
                      className={inputClass + " cursor-pointer flex-1 pr-10"}
                    />
                    <button
                      type="button"
                      onClick={() => setBrowseServiceIndex(i)}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-sm font-medium transition-colors"
                    >
                      Browse
                    </button>
                  </div>
                </Field>

                {/* Features list editor inside the service */}
                <div className="space-y-2 mt-2">
                  <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                    <label className="text-slate-500 font-medium font-manrope text-xs">Features / Bullet Points</label>
                    <button
                      type="button"
                      onClick={() => {
                        const updated = [...sections.services_page.services];
                        updated[i] = { ...updated[i], features: [...(updated[i].features || []), ''] };
                        updateField('services_page', 'services', updated);
                      }}
                      className="text-[#47ace2] text-[11px] font-manrope hover:underline flex items-center gap-0.5 font-semibold"
                    >
                      <Plus className="w-2.5 h-2.5" /> Add Feature
                    </button>
                  </div>
                  {(service.features || []).map((feat, fIdx) => (
                    <div key={fIdx} className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={feat || ''}
                        onChange={e => {
                          const updated = [...sections.services_page.services];
                          const updatedFeats = [...updated[i].features];
                          updatedFeats[fIdx] = e.target.value;
                          updated[i] = { ...updated[i], features: updatedFeats };
                          updateField('services_page', 'services', updated);
                        }}
                        className={inputClass + " py-1.5 text-xs"}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updated = [...sections.services_page.services];
                          updated[i] = {
                            ...updated[i],
                            features: updated[i].features.filter((_, idx) => idx !== fIdx)
                          };
                          updateField('services_page', 'services', updated);
                        }}
                        className="text-slate-300 hover:text-red-500 p-1 rounded-lg hover:bg-red-50"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {browseTeamIndex !== null && (
        <FileManagerModal
          activeUrl={sections.about_page?.team?.[browseTeamIndex]?.image}
          onClose={() => setBrowseTeamIndex(null)}
          onSelect={(url) => {
            const updated = [...(sections.about_page?.team || [])];
            updated[browseTeamIndex] = { ...updated[browseTeamIndex], image: url };
            updateField('about_page', 'team', updated);
            setBrowseTeamIndex(null);
          }}
        />
      )}

      {browseServiceIndex !== null && (
        <FileManagerModal
          activeUrl={sections.services_page?.services?.[browseServiceIndex]?.image}
          onClose={() => setBrowseServiceIndex(null)}
          onSelect={(url) => {
            const updated = [...(sections.services_page?.services || [])];
            updated[browseServiceIndex] = { ...updated[browseServiceIndex], image: url };
            updateField('services_page', 'services', updated);
            setBrowseServiceIndex(null);
          }}
        />
      )}
    </div>
  );
}
